/**
 * A live's strip, across the top of its board where a recording's talk would
 * be: the day and the time with the red dot, how long it runs, when the room
 * opens, and the way to the studio to test the camera. The time opens "Change
 * the time" while the live has not started, with the same controls as New
 * webinar. A live that repeats says its rhythm and its end beside the time,
 * and Change offers this session only or every session from here.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { rhythmLabel } from "../../shared/live-series";
import type { BoardLive, WebinarLive } from "../../shared/types";
import { labelParts, liveChipLabel, roomOpensAt } from "../../shared/live-time";
import { ApiError, api } from "../lib/api";
import { ChangeTime } from "./ChangeTime";
import type { BoardHost } from "./host";
import { RepeatChip } from "./RepeatsFields";
import { ChangeSeries, MoveSession, type SeriesSaved } from "./SeriesDialogs";
import { dayShort } from "./series";
import "../styles/series.css";

const PENCIL = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);

const CAMERA = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="7" width="13" height="10" rx="2" />
    <path d="M16 11l5-3v8l-5-3" />
  </svg>
);

/** Where the host tests the camera and goes live. */
export function studioPath(webinarId: string): string {
  return `/rooms/${webinarId}/studio`;
}

/** "The room opens at 6:50 PM", on the host's own clock. Empty for an unreadable time. */
function roomOpensLine(live: Pick<BoardLive, "startsAt" | "timezone">): string {
  const opens = roomOpensAt(live.startsAt);
  const time = opens ? labelParts(opens, live.timezone, "long")?.time : "";
  return time ? `The room opens at ${time}` : "";
}

/** What Change opens for a series: its two choices, then the popover the host picked. */
type SeriesOpen = "menu" | "one" | "all" | null;

/** Change for a series: this session only, or every session from here. */
function SeriesChange({
  host,
  live,
  open,
  setOpen,
}: {
  host: BoardHost;
  live: BoardLive;
  open: SeriesOpen;
  setOpen: (o: SeriesOpen) => void;
}) {
  const [page, setPage] = useState<WebinarLive | null>(null);
  const menu = useRef<HTMLDivElement>(null);
  const series = live.series;

  useEffect(() => {
    if (open !== "menu") return;
    // The time that opened the menu sits beside it, so a press there is not "away".
    const away = (e: MouseEvent) => {
      const area = menu.current?.parentElement;
      if (area && !area.contains(e.target as Node)) setOpen(null);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open, setOpen]);

  if (!series) return null;
  const saved: SeriesSaved = (res, said) => host.liveChanged(res.board, said);
  const day = dayShort(live.startsAt, live.timezone);

  // Why the webinar's own read: Change the series needs every session and the replay email, which the board does not carry.
  const everySession = () => {
    setOpen(null);
    api.webinars
      .get(host.webinarId)
      .then((detail) => {
        if (!detail.live?.series) return;
        setPage(detail.live);
        setOpen("all");
      })
      .catch((e: unknown) => host.toast(e instanceof ApiError ? e.message : "That did not open. Try again."));
  };

  return (
    <>
      {open === "menu" ? (
        <div className="lchgmenu" role="menu" aria-label="Change" ref={menu}>
          <button type="button" role="menuitem" onClick={() => setOpen("one")}>
            <b>This session only</b>
            <small>{`Only ${day} changes.`}</small>
          </button>
          <button type="button" role="menuitem" onClick={everySession}>
            <b>Every session from here</b>
            <small>The day, the time or how often, from this session on.</small>
          </button>
        </div>
      ) : null}
      {open === "one" ? (
        <MoveSession
          webinarId={host.webinarId}
          session={{
            id: live.sessionId,
            startsAt: live.startsAt,
            lengthMin: live.lengthMin,
            timezone: live.timezone,
            movedFrom: null,
            topic: live.topic,
          }}
          rule={series.rule}
          seats={live.registered}
          onSaved={saved}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === "all" && page ? (
        <ChangeSeries webinarId={host.webinarId} live={page} onSaved={saved} onClose={() => setOpen(null)} />
      ) : null}
    </>
  );
}

export function LiveStrip({ host, live }: { host: BoardHost; live: BoardLive }) {
  const [open, setOpen] = useState(false);
  const [seriesOpen, setSeriesOpen] = useState<SeriesOpen>(null);
  const movable = live.state === "scheduled";
  const series = live.series;
  const when = (
    <>
      <i aria-hidden="true" />
      {`Live · ${liveChipLabel(live.startsAt, live.timezone)} `}
      <span>{`${live.lengthMin} min`}</span>
    </>
  );
  const opens = movable ? roomOpensLine(live) : "";
  const press = () => {
    if (series) setSeriesOpen(seriesOpen === "menu" ? null : "menu");
    else setOpen((was) => !was);
  };
  const seriesChange = movable && series ? (
    <SeriesChange host={host} live={live} open={seriesOpen} setOpen={setSeriesOpen} />
  ) : null;

  return (
    <section className="livestrip" aria-label="The live">
      {movable ? (
        <button
          type="button"
          className="lp"
          title="Change the time"
          aria-haspopup={series ? "menu" : "dialog"}
          aria-expanded={series ? seriesOpen === "menu" : open}
          onClick={press}
        >
          {when}
          {/* The time is the control, so it says so, in the look of the studio's button beside it. */}
          <b className="lpedit">
            {PENCIL}
            Change
          </b>
        </button>
      ) : (
        <span className="lp">{when}</span>
      )}
      {series ? <RepeatChip>{`${rhythmLabel(series.rule)} · ${series.ends}`}</RepeatChip> : null}
      <span className="spgap" />
      {opens ? <span className="spnote">{opens}</span> : null}
      <Link className="sb" to={studioPath(host.webinarId)}>
        {CAMERA}
        Test your setup
      </Link>
      {open && movable && !series ? (
        <ChangeTime live={live} seats={live.registered} onSave={host.moveLive} onClose={() => setOpen(false)} />
      ) : null}
      {seriesChange}
    </section>
  );
}
